import { Tag, Table, PaginationV2 } from '@ver-uds/react';
import { Accordion, Alert } from '@ver-uds/uswds-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import dateFormat from 'dateformat';
import {
  Account,
  AccountSearchParameters,
  AccountStatus,
  AccountType,
  PaginatedAccounts,
  SortDirection,
  SortField,
} from 'eve-account-client';
import type { Column } from 'react-table';
import AriaLink from '../AriaLink/AriaLink';
import LinkType from '../../constants/LinkType';
import useAccountApi from '../../services/AccountApi';
import './AgentTerminationBanner.scss';

interface BannerProps {
  account: Account;
}

type ClientCompanyRow = {
  companyName: string;
  companyNumber: string;
  dateOfTermination: string;
};

const EVerify = (): React.JSX.Element => <span className="no-br">E-Verify</span>;

const ATMIS_CODE = 'ATMIS';
const SUPPORT_EMAIL = 'E-Verify.support@uscis.dhs.gov';

const TABLE_COLUMNS: Column<ClientCompanyRow>[] = [
  {
    Header: 'Company Name',
    accessor: 'companyName',
  },
  {
    Header: 'Company Number',
    accessor: 'companyNumber',
  },
  {
    Header: 'Date of Termination',
    accessor: 'dateOfTermination',
    sortType: 'datetime',
  },
];

const AgentTerminationBanner: React.FC<BannerProps> = ({ account }) => {
  const accountApi = useAccountApi();

  const { companyInfo: company } = account;
  const isPrimaryAccountAtmis =
    (account?.closedReasonCode === ATMIS_CODE || account?.closedReason === ATMIS_CODE) && !!account?.closedDate;

  const isLoading = useRef(false);

  const [doShow, setDoShow] = useState(isPrimaryAccountAtmis);
  const [showLoader, setShowLoader] = useState(false);
  const [isReady, setIsReady] = useState(false);

  const [isOrganizationFlagged, setIsOrganizationFlagged] = useState(false);

  const [tableRows, setTableRows] = useState<ClientCompanyRow[]>([]);
  const [tablePageIndex, setTablePageIndex] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(10);

  const [tableTotalElements, setTableTotalElements] = useState(0);
  const [tableTotalPages, setTableTotalPages] = useState(0);

  const searchAccounts = useCallback(
    async (params: AccountSearchParameters): Promise<PaginatedAccounts> =>
      accountApi.searchAccounts({ accountSearchParameters: params }),
    [accountApi],
  );

  const retrieveData = useCallback(async (): Promise<void> => {
    if (account?.type !== AccountType.AGENCY) {
      isLoading.current = false;
      setDoShow(false);
      setIsReady(true);

      return;
    }

    setShowLoader(true);

    const searchResults = await searchAccounts({
      paging: {
        page: tablePageIndex,
        size: tablePageSize,
      },
      query: {
        statuses: [AccountStatus.ACTIVE],
        closedReasonCode: ATMIS_CODE,
      },
      sort: [{ field: SortField.COMPANYNAME, order: SortDirection.ASC }],
    });

    const { paginated: paginatedSearch } = searchResults;

    let searchTotalElements = 0;

    setIsOrganizationFlagged(isPrimaryAccountAtmis);

    if (paginatedSearch) {
      const { data, totalElements, totalPages } = paginatedSearch;

      searchTotalElements = totalElements;
      setTableRows(
        data.map((acc) => ({
          companyName: acc.companyInfo.companyName ?? '---',
          companyNumber: acc.id ?? '---',
          dateOfTermination: acc.closedDate ? dateFormat(acc.closedDate, 'mm/dd/yyyy') : '---',
        })),
      );

      setTableTotalElements(totalElements);
      setTableTotalPages(totalPages);
    } else {
      setTableRows([]);

      setTableTotalElements(0);
      setTableTotalPages(0);
    }

    isLoading.current = false;

    setDoShow(isPrimaryAccountAtmis || searchTotalElements > 0);
    setShowLoader(false);
    setIsReady(true);
  }, [account?.type, isPrimaryAccountAtmis, searchAccounts, tablePageIndex, tablePageSize]);

  useEffect(() => {
    if (!isLoading.current) {
      isLoading.current = true;
      retrieveData();
    }
  }, [retrieveData]);

  const formattedDate = dateFormat(account?.closedDate, 'longDate');
  const areOnlyClientsFlagged = tableTotalElements > 0 && !isOrganizationFlagged;

  if (!doShow) {
    return <></>;
  }

  return (
    <div className="agent-termination-banner margin-bottom-3 margin-top-2">
      <Alert status="warning">
        <Alert.Heading>
          <EVerify /> intends to terminate one or more accounts for misuse.
        </Alert.Heading>
        <div>
          <p>
            <EVerify /> has attempted to contact your organization on several occasions. Email{' '}
            <AriaLink link={SUPPORT_EMAIL} linkType={LinkType.EMAIL} customLinkText={SUPPORT_EMAIL} /> for assistance.
          </p>

          {isReady && (
            <div>
              Accounts to be terminated include:
              <ul className="margin-0">
                {!areOnlyClientsFlagged && (
                  <li>{`${company.companyName} Employer Agent account (date of termination: ${formattedDate})`}</li>
                )}
                {!isOrganizationFlagged && <li>The Client Company accounts listed below</li>}
              </ul>
            </div>
          )}
        </div>
      </Alert>

      {isReady && !isOrganizationFlagged && (
        <Accordion className="margin-top-2" bordered>
          <Accordion.Heading controls="content">
            <span>Client Companies to be Terminated for Misuse</span>
            <Tag>{tableTotalElements}</Tag>
          </Accordion.Heading>

          <Accordion.Content id="content">
            <div className={showLoader ? 'is-loading-data' : ''}>
              <Table id="client-companies-table" columns={TABLE_COLUMNS} data={tableRows} />
              <div className="data-loading-overlay" />

              {tableTotalPages > 1 && (
                <PaginationV2
                  pageCount={tableTotalPages}
                  pageSize={tablePageSize}
                  pageIndex={tablePageIndex}
                  setPageSize={setTablePageSize}
                  gotoPage={setTablePageIndex}
                />
              )}
            </div>
          </Accordion.Content>
        </Accordion>
      )}
    </div>
  );
};

export default AgentTerminationBanner;
